// src/oura/screens/AllClientDocs.tsx
//
// All clients: the second view on Businesses (docs phase 1, workstream 8).
// Every client's docs in one list, in the morning order: answers to read,
// then what is late (docs past reply-by, and codes past their due date with
// no doc sent), then docs we owe, then docs out with clients, oldest first.
// With every group shown, each doc or code is listed once, in the first card
// it belongs to. Then 1 line per client with its folder, its typical reply
// time and its counts, in the same order. Read-only: a doc row opens that
// business's Client tab with the doc open.
//
// Data: route 11 (`GET /documents/summary`) through useDocsAcrossClients,
// which serves the sample store until the documents backend ships. Loading
// shows placeholders, never a 0. A failed first read says so and retries; a
// failed refresh keeps the rows on screen under a banner. A client whose
// folder is not linked, or not read yet, shows "–", never a 0.
//
// The ?show= filter lives in the URL, so a deep link and Back both land on
// the same list.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    CalendarClockIcon,
    ClipboardListIcon,
    FileTextIcon,
    FolderIcon,
    MessagesSquareIcon,
    RotateCwIcon,
    TimerIcon,
} from "lucide-react";
import type { AcrossClient, AcrossRow } from "@service/aeo/clientDocs";
import {
    agoText,
    displayTitle,
    docCodeChips,
    docDate,
    docLateNote,
    docsFailureText,
    fromAgoText,
    isReadStale,
    needUsText,
    plural,
    readAgoText,
    replyByText,
    trimText,
    useDocsAcrossClients,
    waitingOnText,
} from "@oura/docs";
import { myToday } from "../components/docs/boardModel";
import { useDocCatalogue } from "../components/docs/useDocCatalogue";
import {
    ArrowLink,
    DOC_FLAG,
    DOC_STATUS,
    Dot,
    FamilyChip,
    FOCUS_RING,
    FOLDER_STATUS,
    folderErrorStatus,
    FoldCard,
    FoldScope,
    foldKey,
    IconSquare,
    LATE_TEXT,
    ListRow,
    NARROW_VIEWPORT,
    PillButton,
    readFolded,
    ShowMore,
    StatusKey,
    SURFACE,
    Tag,
    Tile,
    Tiles,
    TYPE,
    useFoldOpener,
    useMediaQuery,
    type PanelIconSpec,
    type Tone,
} from "@oura/ui";
import { EMPTY_CELL, TABLE } from "../components/client/staffScreen";
import { cn } from "../lib/cn";
import { joinDotted } from "../lib/oura";
import { isBusinessSidebarLive } from "../nav";
import {
    GROUP_ORDER,
    clientTones,
    countsKnown,
    factsSentence,
    firstDays,
    folderHref,
    folderState,
    groupCount,
    lateOf,
    morningRows,
    readShow,
    replyTimeOf,
    reviewsHref,
    summarise,
    type GroupKey,
    type NotSentLine,
    type ShowKey,
    type Summary,
} from "./allClients/model";

const SCOPE = "staff-businesses";
/** Rows shown in each group before "Show N more", when the view shows every group. */
const SHOWN = 3;
/** Where the list was scrolled, and which groups were expanded, when a row opened a doc in this tab, so Back lands there again. */
const SCROLL_KEY = "oura.allClients.scroll";

interface SavedList {
    key?: unknown;
    y?: unknown;
    expanded?: unknown;
}

/** What the list looked like when a row opened a doc in this tab, or null. */
function readSavedList(): SavedList | null {
    try {
        const raw = window.sessionStorage.getItem(SCROLL_KEY);
        return raw ? (JSON.parse(raw) as SavedList) : null;
    } catch {
        return null;
    }
}

const AMBER_BLOCK = cn(TYPE.tb, "rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200");

// The card titles are the board's own words (DOC_STATUS), so the two screens never drift.
const GROUP: Record<GroupKey, { id: string; title: string; icon: PanelIconSpec; empty: string }> = {
    "needs-us": {
        id: "docs-needs-us",
        title: DOC_STATUS.needs_us.staff.label,
        icon: { icon: ClipboardListIcon, area: "work" },
        empty: "Nothing needs us. A client's answer shows here.",
    },
    late: {
        id: "docs-late",
        title: "Late",
        icon: { icon: TimerIcon, area: "asks" },
        empty: "Nothing is late. A doc shows here once its reply-by date passes, and a code once its due date passes with no doc sent.",
    },
    "not-sent-yet": {
        id: "docs-not-sent-yet",
        title: DOC_STATUS.not_sent_yet.staff.label,
        icon: { icon: CalendarClockIcon, area: "dates" },
        empty: "Nothing to create. A code shows here once its plan month starts and no doc covering it is shared.",
    },
    waiting: {
        id: "docs-waiting",
        title: DOC_STATUS.waiting_on_client.staff.label,
        icon: { icon: MessagesSquareIcon, area: "asks" },
        empty: "Nothing waits on a client. A shared doc with questions shows here.",
    },
};

/** The filter chips' dots, so the filter is also the legend: the board's tones, and rose for late. */
const GROUP_TONE: Record<GroupKey, Tone> = {
    "needs-us": DOC_STATUS.needs_us.staff.tone,
    late: "rose",
    "not-sent-yet": DOC_STATUS.not_sent_yet.staff.tone,
    waiting: DOC_STATUS.waiting_on_client.staff.tone,
};

type Open = (to: string) => void;
/** Who a row waits on, in the status tag's words. */
type Who = "us" | "client";

/* ------------------------------------------------------------------------- *
 * Small pieces
 * ------------------------------------------------------------------------- */

/** A list still loading: grey placeholder rows, never an empty state. */
function PlaceholderRows({ rows = 2 }: { rows?: number }) {
    return (
        <div aria-hidden>
            {Array.from({ length: rows }, (_, i) => (
                <div key={i} className={cn("flex min-h-12 items-center gap-3 border-t first:border-t-0", SURFACE.hairline)}>
                    <span className="h-2 w-2 rounded-full bg-gray-200" />
                    <span className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                </div>
            ))}
        </div>
    );
}

function Empty({ children }: { children: ReactNode }) {
    return <p className={cn(TYPE.tb, "m-0 text-gray-500")}>{children}</p>;
}

/** An unknown count: a dash on screen, the words for a screen reader. */
function Unknown({ label }: { label: string }) {
    return (
        <>
            <span aria-hidden className="text-gray-400">
                {EMPTY_CELL}
            </span>
            <span className="sr-only">{label}</span>
        </>
    );
}

/** The client's identity: colour dot and name. */
function ClientName({ name, tone, className }: { name: string; tone: Tone; className?: string }) {
    return (
        <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
            <Dot tone={tone} />
            <span className="truncate">{name}</span>
        </span>
    );
}

/** "Sharing flag on 1 doc": the board's red flags, counted per client. */
function flagText(n: number): string {
    return n === 1 ? "Sharing flag on 1 doc" : `Sharing flags on ${n} docs`;
}

/* ------------------------------------------------------------------------- *
 * A doc row
 * ------------------------------------------------------------------------- */

/**
 * A row's late note; null when the doc is not late. A row's lateWorkingDays
 * is set only when the doc is late; 0 (the weekend after its reply-by) reads
 * "Was due …". On a doc that waits on us the note says the lateness is the
 * client's, in the board's words (docLateNote).
 */
function lateNote(row: AcrossRow, who: Who): string | null {
    return docLateNote({ late: row.lateWorkingDays !== null, lateWorkingDays: row.lateWorkingDays, replyBy: row.replyBy }, who);
}

/**
 * Clients whose folder Oura can no longer read, by id, with the last good
 * read their rows and counts come from (null when there was none).
 */
type LastGood = ReadonlyMap<string, string | null>;

/** "Last good read on Fri 25 Sep": a row built from a folder Oura can no longer read. Never shown as current. */
function lastGoodText(iso: string | null): string {
    const ago = agoText(iso);
    return ago ? `Last good read ${ago}` : "Can't read the folder";
}

/**
 * The stale clause, amber, ahead of a row's own facts; the facts alone when
 * the folder reads fine. Each fact keeps the dot in front of it, as on the
 * board's rows, so a wrapped line never ends on a "·".
 */
function StaleMeta({ lastGoodAt, meta, className = "mt-1" }: { lastGoodAt: string | null | undefined; meta: Array<string | null | undefined | false>; className?: string }) {
    const stale = lastGoodAt === undefined ? null : lastGoodText(lastGoodAt);
    const parts: ReactNode[] = [stale && <span className="font-medium text-amber-800">{stale}</span>, ...meta.map((m) => (m ? m.trim() : null))].filter(Boolean);
    if (parts.length === 0) return null;
    return (
        <span className={cn(className, "flex flex-wrap gap-x-2 tabular-nums")}>
            {parts.map((p, i) => (
                <span key={i} className="inline-flex gap-x-2">
                    {i > 0 && <span aria-hidden className="text-gray-400">·</span>}
                    {p}
                </span>
            ))}
        </span>
    );
}

function DocRow({ row, tone, who, lastGoodAt, onOpen }: {
    row: AcrossRow;
    tone: Tone;
    who: Who;
    /** Set when the client's folder cannot be read: the row is from this read. */
    lastGoodAt?: string | null;
    onOpen: Open;
}) {
    const title = displayTitle(row.title);
    const chips = docCodeChips(row);
    const late = lateNote(row, who);
    // "7 out · 3 back · Waiting on us for 6 working days": the same words as the board. Null when the comments were not read.
    const questions = row.questions ? `${row.questions.out} out · ${row.questions.back} back` : null;
    // The reply-by is the client's date: shown, as on the board, only while the doc waits on them and is not late yet.
    const meta = [questions, waitingOnText(who, row.waitingWorkingDays), who === "client" && !late ? replyByText(row.replyBy) : null];
    const flags = row.redFlags ?? [];
    const words = row.lastClientPost?.text.trim();
    return (
        <ListRow
            data-doc={row.documentId}
            onClick={() => onOpen(reviewsHref(row.businessId, row.documentId))}
            icon={<IconSquare tone="blue" icon={FileTextIcon} />}
            title={title}
            sub={(
                <>
                    <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <ClientName name={row.businessName} tone={tone} className="text-gray-600" />
                        {chips.length > 0 && (
                            <span className="inline-flex flex-wrap gap-1">
                                {chips.map((c) => (
                                    <FamilyChip key={c.label} family={c.family}>
                                        {c.label}
                                    </FamilyChip>
                                ))}
                            </span>
                        )}
                        {/* The sharing flags on this doc, as the Client tab row shows them. */}
                        {flags.map((code) => (
                            <Tag key={code} tone="red">
                                {DOC_FLAG[code]?.label ?? "Sharing flag"}
                            </Tag>
                        ))}
                    </span>
                    <StaleMeta lastGoodAt={lastGoodAt} meta={meta} />
                    {/* On a phone the late note sits under the row: beside it, it squeezes the title to a word. */}
                    {late && <span className={cn("mt-0.5 block tabular-nums sm:hidden", LATE_TEXT)}>{late}</span>}
                    {words && <span className="mt-1 line-clamp-2 break-words text-gray-600">&ldquo;{trimText(words, 200)}&rdquo;</span>}
                </>
            )}
            right={late ? <span className={cn(TYPE.tc, LATE_TEXT, "hidden shrink-0 whitespace-nowrap tabular-nums sm:inline")}>{late}</span> : undefined}
        />
    );
}

/* ------------------------------------------------------------------------- *
 * The group cards
 * ------------------------------------------------------------------------- */

function DocGroupCard({
    group,
    rows,
    codeLines = [],
    elsewhere,
    loading,
    showAll,
    foldedFact,
    tones,
    whoFor,
    lastGood,
    expanded,
    onExpand,
    onOpen,
}: {
    group: Exclude<GroupKey, "not-sent-yet">;
    rows: AcrossRow[];
    /** Late only: each client's codes past their due date with no doc sent, after the late docs. */
    codeLines?: NotSentLine[];
    /** This group's docs listed in an earlier card instead, in words; null when none. The All view only. */
    elsewhere: string | null;
    loading: boolean;
    showAll: boolean;
    foldedFact: string | null;
    tones: Map<string, Tone>;
    /** Who a row waits on: us for a doc that needs us, the client otherwise. */
    whoFor: (r: AcrossRow) => Who;
    lastGood: LastGood;
    /** "Show N more" is open: kept by the screen, so Back puts the list back as it was. */
    expanded: boolean;
    onExpand: (open: boolean) => void;
    onOpen: Open;
}) {
    const g = GROUP[group];
    // The late docs, then the late codes: one list, so "Show N more" counts both.
    const items: Array<{ doc: AcrossRow } | { line: NotSentLine }> = [...rows.map((doc) => ({ doc })), ...codeLines.map((line) => ({ line }))];
    const shown = showAll ? items : items.slice(0, SHOWN);
    const rest = showAll ? [] : items.slice(SHOWN);
    const row = (it: { doc: AcrossRow } | { line: NotSentLine }) =>
        "doc" in it ? (
            <DocRow
                key={`${group}:${it.doc.documentId}`}
                row={it.doc}
                tone={tones.get(it.doc.businessId) ?? "gray"}
                who={whoFor(it.doc)}
                lastGoodAt={lastGood.get(it.doc.businessId)}
                onOpen={onOpen}
            />
        ) : (
            <NotSentLineRow
                key={`${group}:codes:${it.line.client.businessId}`}
                line={it.line}
                late
                tone={tones.get(it.line.client.businessId) ?? "gray"}
                lastGoodAt={lastGood.get(it.line.client.businessId)}
                onOpen={onOpen}
            />
        );
    const codes = codeLines.reduce((n, l) => n + l.count, 0);
    const count = [rows.length > 0 || codes === 0 ? plural(rows.length, "doc") : null, codes > 0 ? plural(codes, "code") : null].filter(Boolean).join(" · ");
    return (
        <FoldCard
            id={g.id}
            title={g.title}
            icon={g.icon}
            right={loading ? undefined : <span className="tabular-nums">{count}</span>}
            foldedFact={loading ? undefined : foldedFact}
        >
            {loading ? (
                <PlaceholderRows />
            ) : items.length === 0 ? (
                // Every doc in this group shows in an earlier card: say where, never "Nothing is late".
                <Empty>{elsewhere ?? g.empty}</Empty>
            ) : (
                <div>
                    {shown.map(row)}
                    <ShowMore count={rest.length} open={expanded} onToggle={onExpand}>
                        {rest.map(row)}
                    </ShowMore>
                    {elsewhere && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>{elsewhere}</p>}
                </div>
            )}
        </FoldCard>
    );
}

/**
 * The earliest due date among a client's codes not sent yet, in the board's
 * words: "Due by Fri 16 Oct", or "Was due Tue 22 Sep" once that last working
 * day has passed (Malaysian time). Null with no date.
 */
function notSentDueText(dueBy: string | null): string | null {
    const d = docDate(dueBy);
    if (!d || !dueBy) return null;
    return dueBy < myToday() ? `Was due ${d}` : `Due by ${d}`;
}

/**
 * One client's codes not sent yet, in a Late or Not sent yet card: how many,
 * when the first was due, and the docs that clear them ("Answer Plan (S1,
 * S3, B1)") when the server names the codes. `late`: the line holds codes
 * past their due date, in the Late card.
 */
function NotSentLineRow({ line, late, tone, lastGoodAt, onOpen }: { line: NotSentLine; late: boolean; tone: Tone; lastGoodAt: string | null | undefined; onOpen: Open }) {
    const due = notSentDueText(line.dueBy);
    const overdue = !!line.dueBy && line.dueBy < myToday();
    return (
        <ListRow
            data-business={line.client.businessId}
            onClick={() => onOpen(reviewsHref(line.client.businessId))}
            icon={<Dot tone={tone} />}
            title={line.client.name}
            sub={(
                <>
                    <StaleMeta
                        className=""
                        lastGoodAt={lastGoodAt}
                        meta={[late ? `${plural(line.count, "code")} not sent` : `${plural(line.count, "code")} to create`]}
                    />
                    {line.docs && line.docs.length > 0 && <span className="mt-0.5 block break-words text-gray-600">{line.docs.join(", ")}</span>}
                    {/* On a phone the due date sits under the row, as a late doc's note does. */}
                    {due && <span className={cn("mt-0.5 block tabular-nums sm:hidden", overdue && LATE_TEXT)}>{due}</span>}
                </>
            )}
            right={due ? <span className={cn(TYPE.tc, "hidden shrink-0 whitespace-nowrap tabular-nums sm:inline", overdue ? LATE_TEXT : "text-gray-500")}>{due}</span> : undefined}
        />
    );
}

function NotSentCard({
    lines,
    elsewhere,
    loading,
    showAll,
    tones,
    lastGood,
    expanded,
    onExpand,
    onOpen,
}: {
    lines: NotSentLine[];
    /** The late codes listed under Late instead, in words; null when none. The All view only. */
    elsewhere: string | null;
    loading: boolean;
    showAll: boolean;
    tones: Map<string, Tone>;
    lastGood: LastGood;
    expanded: boolean;
    onExpand: (open: boolean) => void;
    onOpen: Open;
}) {
    const g = GROUP["not-sent-yet"];
    const total = lines.reduce((n, l) => n + l.count, 0);
    const shown = showAll ? lines : lines.slice(0, SHOWN);
    const rest = showAll ? [] : lines.slice(SHOWN);
    const row = (l: NotSentLine) => (
        <NotSentLineRow
            key={l.client.businessId}
            line={l}
            late={false}
            tone={tones.get(l.client.businessId) ?? "gray"}
            lastGoodAt={lastGood.get(l.client.businessId)}
            onOpen={onOpen}
        />
    );
    return (
        <FoldCard
            id={g.id}
            title={g.title}
            icon={g.icon}
            right={loading ? undefined : <span className="tabular-nums">{plural(total, "code")}</span>}
            foldedFact={loading || lines.length === 0 ? undefined : plural(lines.length, "client")}
        >
            {loading ? (
                <PlaceholderRows />
            ) : lines.length === 0 ? (
                <Empty>{elsewhere ?? g.empty}</Empty>
            ) : (
                <div>
                    {shown.map(row)}
                    <ShowMore count={rest.length} open={expanded} onToggle={onExpand}>
                        {rest.map(row)}
                    </ShowMore>
                    {elsewhere && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>{elsewhere}</p>}
                </div>
            )}
        </FoldCard>
    );
}

/* ------------------------------------------------------------------------- *
 * Clients: the one table
 * ------------------------------------------------------------------------- */

/** Clients that need something first; clients Oura cannot count last. */
function byUrgency(a: AcrossClient, b: AcrossClient): number {
    const ka = countsKnown(a);
    const kb = countsKnown(b);
    if (ka !== kb) return ka ? -1 : 1;
    // The morning order: needs us, late (docs and codes), not sent yet, then waiting on the client.
    return b.needsUs - a.needsUs || lateOf(b) - lateOf(a) || b.notSentYet - a.notSentYet || b.waitingOnClient - a.waitingOnClient || a.name.localeCompare(b.name);
}

function FolderStatus({ client, onOpen }: { client: AcrossClient; onOpen: Open }) {
    const state = folderState(client);
    if (state === "not_linked") {
        return (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Tag tone={FOLDER_STATUS.not_linked.tone}>{FOLDER_STATUS.not_linked.label}</Tag>
                <ArrowLink onClick={() => onOpen(folderHref(client.businessId))}>Link the folder</ArrowLink>
            </span>
        );
    }
    if (state === "cant_read") {
        const failed = FOLDER_STATUS[folderErrorStatus(client.folderError?.code)];
        return (
            <span className="block">
                <Tag tone={failed.tone}>{failed.label}</Tag>
                {client.folderError?.message && (
                    <span className={cn(TYPE.tm, "mt-1 block break-words text-gray-600")}>{client.folderError.message}</span>
                )}
                {/* Beside the counts at every width (below 1280px this sits in the client cell): they are from this read, not now. */}
                {fromAgoText(client.readAt) && (
                    <span className={cn(TYPE.tm, "mt-1 block font-medium text-amber-800")}>Counts from {fromAgoText(client.readAt)}</span>
                )}
            </span>
        );
    }
    const words = state === "not_read" ? FOLDER_STATUS.not_read : FOLDER_STATUS.connected;
    return <Tag tone={words.tone}>{words.label}</Tag>;
}

/** A count in the table: a dash when nobody has measured it, grey for 0, rose for late. */
function CountCell({ client, n, late, label }: { client: AcrossClient; n: number; late?: boolean; label: string }) {
    if (!countsKnown(client)) return <Unknown label={`${label}: not measured`} />;
    return <span className={cn("tabular-nums", n === 0 ? "text-gray-400" : late ? LATE_TEXT : "text-gray-900")}>{n}</span>;
}

function ClientButton({ client, tone, onOpen }: { client: AcrossClient; tone: Tone; onOpen: Open }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(reviewsHref(client.businessId))}
            aria-label={`${client.name}, open the Client tab`}
            className={cn(
                TYPE.tb,
                "inline-flex min-w-0 max-w-full items-center rounded text-left font-medium text-gray-900 hover:text-blue-600",
                // 20px of text, 44px to a thumb: 20 + 2 x 12.
                "relative after:absolute after:-inset-x-1 after:-inset-y-3 after:content-['']",
                FOCUS_RING,
            )}
        >
            <ClientName name={client.name} tone={tone} />
        </button>
    );
}

function ClientsTable({ clients, tones, onOpen }: { clients: AcrossClient[]; tones: Map<string, Tone>; onOpen: Open }) {
    return (
        <div className={TABLE.scroll}>
            {/* Client is capped and the 4 counts share the rest evenly. With Client the
                only column left free it took all the slack: on a wide screen the names
                sat 1,100px from their numbers, with nothing in between (YW, 15 Oct 2026). */}
            <table className="w-full border-collapse">
                <thead>
                    <tr>
                        <th className={cn(TABLE.th, "w-[30%] text-left")}>Client</th>
                        <th className={cn(TABLE.th, "hidden w-[184px] text-left xl:table-cell")}>Folder</th>
                        <th className={cn(TABLE.th, "w-[176px] text-left")}>Typical reply time</th>
                        <th className={cn(TABLE.th, "w-[10%] text-center")}>Needs us</th>
                        <th className={cn(TABLE.th, "w-[10%] text-center")}>Late</th>
                        <th className={cn(TABLE.th, "w-[10%] text-center")}>To create</th>
                        <th className={cn(TABLE.th, "w-[10%] text-center")}>Waiting on client</th>
                    </tr>
                </thead>
                <tbody>
                    {clients.map((c) => {
                        const reply = replyTimeOf(c);
                        const flags = countsKnown(c) ? c.redFlags ?? 0 : 0;
                        return (
                            // The whole row opens the Client tab, as on the Businesses table. The name
                            // stays the control for keyboards and screen readers. A click on the name or
                            // "Link the folder" already opens its own place, so the row leaves it alone
                            // (otherwise 2 tabs open, and the folder link loses its ?folder=1).
                            <tr
                                key={c.businessId}
                                data-business={c.businessId}
                                className="cursor-pointer transition-colors hover:bg-gray-50"
                                onClick={(e) => {
                                    if ((e.target as HTMLElement).closest("a, button")) return;
                                    onOpen(reviewsHref(c.businessId));
                                }}
                            >
                                <td className={cn(TABLE.td, "py-2.5")}>
                                    <ClientButton client={c} tone={tones.get(c.businessId) ?? "gray"} onOpen={onOpen} />
                                    {flags > 0 && (
                                        <span className="mt-1 block">
                                            <Tag tone="red">{flagText(flags)}</Tag>
                                        </span>
                                    )}
                                    {/* Below 1280px the folder column folds into this cell. */}
                                    <span className="mt-1 block xl:hidden">
                                        <FolderStatus client={c} onOpen={onOpen} />
                                    </span>
                                </td>
                                <td className={cn(TABLE.td, "hidden py-2.5 xl:table-cell")}>
                                    <FolderStatus client={c} onOpen={onOpen} />
                                </td>
                                <td className={cn(TABLE.td, "py-2.5")}>
                                    <span className={cn(TYPE.tb, "block tabular-nums text-gray-900")}>
                                        {reply.value ?? <Unknown label="Not measured" />}
                                    </span>
                                    {reply.basis && <span className={cn(TYPE.tm, "block text-gray-500")}>{reply.basis}</span>}
                                </td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-center")}>
                                    <CountCell client={c} n={c.needsUs} label="Needs us" />
                                </td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-center")}>
                                    {/* Docs past reply-by and codes past their due date. */}
                                    <CountCell client={c} n={lateOf(c)} late label="Late" />
                                </td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-center")}>
                                    <CountCell client={c} n={c.notSentYet} label="To create" />
                                </td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-center")}>
                                    <CountCell client={c} n={c.waitingOnClient} label="Waiting on client" />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

/** Below 1024px the table becomes 1 stacked block per client: the columns would squeeze to nothing. */
function ClientsStacked({ clients, tones, onOpen }: { clients: AcrossClient[]; tones: Map<string, Tone>; onOpen: Open }) {
    return (
        <div className="px-5 pb-2">
            {clients.map((c) => {
                const reply = replyTimeOf(c);
                const known = countsKnown(c);
                const flags = known ? c.redFlags ?? 0 : 0;
                const replyLine = reply.value && reply.value !== "Never replied" ? `Replies in ${reply.value}` : reply.value;
                const counts = known
                    ? [
                        { key: "n", text: needUsText(c.needsUs), late: false },
                        { key: "l", text: `${lateOf(c)} late`, late: lateOf(c) > 0 },
                        { key: "s", text: `${c.notSentYet} to create`, late: false },
                        { key: "w", text: `${c.waitingOnClient} waiting on client`, late: false },
                    ]
                    : [];
                return (
                    <div key={c.businessId} data-business={c.businessId} className={cn("border-t py-3 first:border-t-0", SURFACE.hairline)}>
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                            <ClientButton client={c} tone={tones.get(c.businessId) ?? "gray"} onOpen={onOpen} />
                            {flags > 0 && <Tag tone="red">{flagText(flags)}</Tag>}
                        </div>
                        <div className="mt-1.5">
                            <FolderStatus client={c} onOpen={onOpen} />
                        </div>
                        {(replyLine || reply.basis) && (
                            <p className={cn(TYPE.tc, "m-0 mt-1.5 tabular-nums text-gray-500")}>{joinDotted(replyLine, reply.basis)}</p>
                        )}
                        {counts.length > 0 && (
                            <p className={cn(TYPE.tc, "m-0 mt-1 grid grid-cols-2 gap-x-4 tabular-nums text-gray-500")}>
                                {counts.map((p) => (
                                    <span key={p.key} className={cn("min-w-0", p.late && LATE_TEXT)}>
                                        {p.text}
                                    </span>
                                ))}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ClientsCard({
    clients,
    loading,
    note,
    tones,
    onOpen,
}: {
    clients: AcrossClient[];
    loading: boolean;
    note: string | null;
    tones: Map<string, Tone>;
    onOpen: Open;
}) {
    const narrow = useMediaQuery(NARROW_VIEWPORT);
    const sorted = useMemo(() => [...clients].sort(byUrgency), [clients]);
    const notLinked = clients.filter((c) => !c.folderLinked).length;
    return (
        <FoldCard
            id="docs-clients"
            title="Clients"
            icon={{ icon: FolderIcon, area: "setup" }}
            flush
            right={loading ? undefined : <span className="tabular-nums">{plural(clients.length, "client")}</span>}
            foldedFact={loading || notLinked === 0 ? undefined : `${notLinked} not linked`}
        >
            {loading ? (
                <div className="px-5 pb-3">
                    <PlaceholderRows rows={3} />
                </div>
            ) : (
                <>
                    {note && <p className={cn(TYPE.tb, "m-0 px-5 pb-3 text-gray-500")}>{note}</p>}
                    {narrow ? <ClientsStacked clients={sorted} tones={tones} onOpen={onOpen} /> : <ClientsTable clients={sorted} tones={tones} onOpen={onOpen} />}
                </>
            )}
        </FoldCard>
    );
}

/* ------------------------------------------------------------------------- *
 * The view
 * ------------------------------------------------------------------------- */

/**
 * "Up to 3 working days late" ("12 working days late" for 1 doc), or "Past
 * reply-by" when every late doc is 0 working days late (the weekend after);
 * null with none late. The rows arrive most late first.
 */
function lateSummaryText(rows: readonly AcrossRow[]): string | null {
    if (rows.length === 0) return null;
    const most = firstDays(rows, (r) => r.lateWorkingDays);
    if (!most) return "Past reply-by";
    return rows.length === 1 ? `${plural(most, "working day")} late` : `Up to ${plural(most, "working day")} late`;
}

/** Late in short: "Up to 3 working days late · 1 code not sent". Null with nothing late. */
function lateFact(rows: readonly AcrossRow[], codes: number): string | null {
    const parts = [lateSummaryText(rows), codes > 0 ? `${plural(codes, "code")} not sent` : null].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
}

/** A folded card's fact, from the rows the card lists (oldest first), so it never describes a doc listed in another card. */
function foldedFactOf(group: Exclude<GroupKey, "not-sent-yet">, rows: readonly AcrossRow[], codes = 0): string | null {
    if (group === "late") return lateFact(rows, codes);
    const days = firstDays(rows, (r) => r.waitingWorkingDays);
    return days === null ? null : oldestText(days);
}

/** "1 late doc also needs us, so it shows under Needs us." The All view's note on the docs a card leaves to an earlier one. */
function elsewhereText(group: "late" | "waiting", n: number): string | null {
    if (n <= 0) return null;
    const under = group === "late" ? GROUP["needs-us"].title : GROUP.late.title;
    if (group === "late") return n === 1 ? `1 late doc also needs us, so it shows under ${under}.` : `${n} late docs also need us, so they show under ${under}.`;
    return n === 1 ? `1 doc waiting on a client is late, so it shows under ${under}.` : `${n} docs waiting on clients are late, so they show under ${under}.`;
}

/** "1 late code shows under Late.": the All view's note on the late codes the Not sent yet card leaves to Late. */
function lateCodesElsewhere(n: number): string | null {
    if (n <= 0) return null;
    return n === 1 ? `1 late code shows under ${GROUP.late.title}.` : `${n} late codes show under ${GROUP.late.title}.`;
}

/** The Late tile's line: how late the docs are, and how many codes are past due. */
function lateTileSub(s: Summary): ReactNode {
    const docs = s.late.length;
    const codes = s.lateCodesTotal;
    if (docs === 0 && codes === 0) return "Nothing late";
    const text = docs > 0 && codes > 0
        ? `${plural(docs, "doc")} · ${plural(codes, "code")} not sent`
        : docs > 0
            ? lateSummaryText(s.late)
            : `${plural(codes, "code")} not sent`;
    return <span className={LATE_TEXT}>{text}</span>;
}

/**
 * The Not sent yet tile's line: how many of the codes are already past due (in
 * red), then when the next one still in time is due. Never one date for every
 * code: most are usually still in time.
 */
function notSentTileSub(s: Summary): ReactNode {
    const nextDue = docDate(s.notSentInTime[0]?.dueBy ?? null);
    const pastDue = s.lateCodesTotal;
    if (pastDue === 0 && !nextDue) return plural(s.notSent.length, "client");
    return (
        <>
            {pastDue > 0 && <span className={LATE_TEXT}>{pastDue} past due</span>}
            {pastDue > 0 && nextDue && " · "}
            {nextDue && `${pastDue > 0 ? "next" : "Next"} due ${nextDue}`}
        </>
    );
}

/**
 * The typical reply time across clients, over the Clients table: the median
 * of each client's own, and what it rests on. Null when no client has
 * answered a doc, where each row already says so.
 */
function typicalReplyNote(s: Summary): string | null {
    if (s.typicalReply === null || s.typicalReplyClients === 0) return null;
    const basis = s.typicalReplyClients === 1 ? "from 1 client" : `the median across ${s.typicalReplyClients} clients`;
    return `Typical reply time: ${plural(s.typicalReply, "working day")}, ${basis}.`;
}

/** "Oldest 12 working days", or "All under 1 working day": 0 working days is not "today" on a weekend. */
function oldestText(days: number): string {
    return days <= 0 ? "All under 1 working day" : `Oldest ${plural(days, "working day")}`;
}

export function AllClientDocs() {
    const { status, data, error, loadedAt, reload } = useDocsAcrossClients();
    const [params, setParams] = useSearchParams();
    const show = readShow(params.get("show"));
    const navigate = useNavigate();
    const location = useLocation();
    const openCard = useFoldOpener(SCOPE);

    // Read once, before the restore below clears it: Back puts "Show N more" back as it was left.
    const [saved] = useState(readSavedList);
    const [expanded, setExpanded] = useState<GroupKey[]>(() =>
        saved && saved.key === location.key && Array.isArray(saved.expanded)
            ? saved.expanded.filter((k): k is GroupKey => (GROUP_ORDER as unknown[]).includes(k))
            : [],
    );
    const expandFor = (key: GroupKey) => (open: boolean) =>
        setExpanded((was) => (open ? (was.includes(key) ? was : [...was, key]) : was.filter((k) => k !== key)));

    // The summary names the doc each not-sent code goes out in: rebuilt when the catalogue changes.
    const catalogue = useDocCatalogue();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- summarise reads the active catalogue, not this value
    const summary = useMemo(() => (data ? summarise(data) : null), [data, catalogue]);
    // With every group shown, each doc once: a late doc sits under Needs us or Late, not twice.
    const morning = useMemo(() => (summary ? morningRows(summary) : null), [summary]);
    // A doc that needs us waits on us, in any card it shows in (a late doc can need us too).
    const needsUsIds = useMemo(() => new Set(summary?.needsUs.map((r) => r.documentId) ?? []), [summary]);
    const whoFor = useCallback((r: AcrossRow): Who => (needsUsIds.has(r.documentId) ? "us" : "client"), [needsUsIds]);
    // Clients whose folder Oura cannot read: their rows are from the last good read, and say so.
    const lastGood = useMemo<LastGood>(
        () => new Map((data?.clients ?? []).filter((c) => folderState(c) === "cant_read").map((c) => [c.businessId, c.readAt ?? null] as const)),
        [data],
    );
    const tones = useMemo(
        () => clientTones([...(data?.clients.map((c) => c.businessId) ?? []), ...(data ? [...data.needsUs, ...data.waitingOnClient, ...data.late].map((r) => r.businessId) : [])]),
        [data],
    );

    /**
     * Open a business's Client tab the way the Businesses table opens a
     * business: its own tab while the business sidebar is live, so this list
     * stays where it was; otherwise in this tab. Either way the scroll and the
     * expanded groups are noted for Back: a browser that opens the new tab in
     * this one (an embedded preview) comes back to a fresh load of this list.
     */
    const open = useCallback<Open>(
        (to) => {
            try {
                window.sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ key: location.key, y: window.scrollY, expanded }));
            } catch {
                /* private window: Back lands at the top instead */
            }
            if (isBusinessSidebarLive) {
                window.open(to, "_blank", "noopener");
                return;
            }
            navigate(to);
        },
        [location.key, navigate, expanded],
    );

    // Back from a doc opened in this tab: return to where the list was, once it has rows to scroll through.
    // The groups it had expanded are open again on this same render, so the offset lands on the same rows.
    const restored = useRef(false);
    useEffect(() => {
        if (restored.current || status !== "ready") return;
        restored.current = true;
        try {
            window.sessionStorage.removeItem(SCROLL_KEY);
        } catch {
            /* private window: nothing was saved */
        }
        if (saved && saved.key === location.key && typeof saved.y === "number") window.scrollTo({ top: saved.y });
    }, [status, location.key, saved]);

    // A tile, or a filter onto a folded card, opens that card once the list has re-rendered to the new filter.
    const revealNext = useRef<GroupKey | null>(null);
    useEffect(() => {
        const key = revealNext.current;
        if (!key || key !== show) return;
        revealNext.current = null;
        openCard(GROUP[key].id);
    }, [show, openCard]);

    const pick = (key: ShowKey, reveal: boolean) => {
        if (key !== "all" && (reveal || readFolded(foldKey(SCOPE, GROUP[key].id)))) {
            if (key === show) {
                openCard(GROUP[key].id);
                return;
            }
            revealNext.current = key;
        }
        setParams(
            (p) => {
                if (key === "all") p.delete("show");
                else p.set("show", key);
                return p;
            },
            { replace: true },
        );
    };

    const loading = status === "loading";

    // First read failed: say so, retry, and draw nothing that could read as a 0.
    if (status === "error" && !data) {
        return (
            <div role="alert" className={cn(AMBER_BLOCK, "flex flex-wrap items-center justify-between gap-3")}>
                <span className="min-w-0">{error ? docsFailureText(error) : "Could not read the documents."}</span>
                {error?.reason === "network" && (
                    <PillButton size="sm" icon={RotateCwIcon} onClick={reload}>
                        Try again
                    </PillButton>
                )}
            </div>
        );
    }

    // Nothing Oura can count yet: no clients, no folder linked, or none read. The clients list is the way in.
    const countable = loading || (summary !== null && summary.known > 0);
    const setupNote = summary && summary.known === 0 && summary.clients > 0
        ? summary.linked === 0
            ? "Link each client's Drive folder once, on its Client tab."
            : "Each folder shows here after Oura's first read."
        : null;
    // The filter is also the legend and hides empty groups, so a chosen group that is empty shows All.
    const active: ShowKey = show !== "all" && summary && groupCount(summary, show) === 0 ? "all" : show;
    const everyGroup = active === "all";
    const visible = (key: GroupKey) => everyGroup || active === key;
    const synced = data?.syncedAt ?? null;
    // A filtered view lists the whole group; the All view lists each doc once and says where the rest are.
    const needsUsRows = (everyGroup ? morning?.needsUs : summary?.needsUs) ?? [];
    const lateRows = (everyGroup ? morning?.late : summary?.late) ?? [];
    const waitingRows = (everyGroup ? morning?.waiting : summary?.waiting) ?? [];

    return (
        <FoldScope value={SCOPE}>
            <div className="space-y-5" aria-busy={loading}>
                {error && data && (
                    <div role="alert" className={AMBER_BLOCK}>
                        {docsFailureText(error)}
                        {fromAgoText(loadedAt) && ` This list is from ${fromAgoText(loadedAt)}.`}
                    </div>
                )}
                {!error && summary && summary.linked > 0 && isReadStale(synced) && (
                    <div role="status" className={AMBER_BLOCK}>
                        Oura last read the client folders {agoText(synced)}.
                    </div>
                )}

                {summary ? (
                    <p className={cn(TYPE.ts, "m-0")}>{factsSentence(summary)}</p>
                ) : (
                    <div aria-hidden className="h-[26px] w-2/3 max-w-md animate-pulse rounded-md bg-gray-100" />
                )}

                {countable && (
                    <>
                        {/* The morning order, as the cards and the filter below: Needs us, Late, To create, then Waiting on clients. */}
                        <Tiles cols={4}>
                            <Tile
                                label="Needs us"
                                icon={{ icon: ClipboardListIcon, area: "work" }}
                                loading={loading}
                                value={summary?.needsUs.length ?? 0}
                                sub={summary && (summary.oldestNeedsUs !== null ? oldestText(summary.oldestNeedsUs) : "Nothing to read")}
                                onClick={summary ? () => pick("needs-us", true) : undefined}
                            />
                            <Tile
                                label="Late"
                                icon={{ icon: TimerIcon, area: "asks" }}
                                loading={loading}
                                // Docs past reply-by and codes past their due date (YW, 29 Sep 2026).
                                value={summary ? groupCount(summary, "late") : 0}
                                sub={summary && lateTileSub(summary)}
                                onClick={summary ? () => pick("late", true) : undefined}
                            />
                            <Tile
                                label="To create"
                                icon={{ icon: CalendarClockIcon, area: "dates" }}
                                loading={loading}
                                value={summary?.notSentTotal ?? 0}
                                sub={summary && (summary.notSentTotal > 0 ? notSentTileSub(summary) : "Nothing to create")}
                                onClick={summary ? () => pick("not-sent-yet", true) : undefined}
                            />
                            <Tile
                                label="Waiting on clients"
                                icon={{ icon: MessagesSquareIcon, area: "asks" }}
                                loading={loading}
                                value={summary?.waiting.length ?? 0}
                                sub={summary && (summary.oldestWaiting !== null ? oldestText(summary.oldestWaiting) : "Nothing out")}
                                onClick={summary ? () => pick("waiting", true) : undefined}
                            />
                        </Tiles>

                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                            {/* The filter is also the legend. "All" carries no count: Late overlaps
                                the other groups, and To create counts codes, not docs. */}
                            <StatusKey
                                className="min-w-0"
                                ariaLabel="Show documents"
                                value={active}
                                onChange={(k) => pick(k as ShowKey, false)}
                                total={null}
                                groups={GROUP_ORDER.map((k) => ({ key: k, label: GROUP[k].title, tone: GROUP_TONE[k], count: summary ? groupCount(summary, k) : 0 }))}
                            />
                            {data && <span className={cn(TYPE.tc, "text-gray-500")}>{readAgoText(synced)}</span>}
                        </div>

                        {visible("needs-us") && (
                            <DocGroupCard
                                group="needs-us"
                                rows={needsUsRows}
                                elsewhere={null}
                                loading={loading}
                                showAll={!everyGroup}
                                foldedFact={foldedFactOf("needs-us", needsUsRows)}
                                tones={tones}
                                whoFor={whoFor}
                                lastGood={lastGood}
                                expanded={expanded.includes("needs-us")}
                                onExpand={expandFor("needs-us")}
                                onOpen={open}
                            />
                        )}
                        {visible("late") && (
                            <DocGroupCard
                                group="late"
                                rows={lateRows}
                                codeLines={summary?.lateCodes ?? []}
                                elsewhere={everyGroup && morning ? elsewhereText("late", morning.lateUnderNeedsUs) : null}
                                loading={loading}
                                showAll={!everyGroup}
                                foldedFact={foldedFactOf("late", lateRows, summary?.lateCodesTotal ?? 0)}
                                tones={tones}
                                whoFor={whoFor}
                                lastGood={lastGood}
                                expanded={expanded.includes("late")}
                                onExpand={expandFor("late")}
                                onOpen={open}
                            />
                        )}
                        {visible("not-sent-yet") && (
                            <NotSentCard
                                // The All view lists a late code once, under Late; the filter lists every code owed.
                                lines={(everyGroup ? summary?.notSentInTime : summary?.notSent) ?? []}
                                elsewhere={everyGroup && summary ? lateCodesElsewhere(summary.lateCodesTotal) : null}
                                loading={loading}
                                showAll={!everyGroup}
                                tones={tones}
                                lastGood={lastGood}
                                expanded={expanded.includes("not-sent-yet")}
                                onExpand={expandFor("not-sent-yet")}
                                onOpen={open}
                            />
                        )}
                        {visible("waiting") && (
                            <DocGroupCard
                                group="waiting"
                                rows={waitingRows}
                                elsewhere={everyGroup && morning ? elsewhereText("waiting", morning.waitingUnderLate) : null}
                                loading={loading}
                                showAll={!everyGroup}
                                foldedFact={foldedFactOf("waiting", waitingRows)}
                                tones={tones}
                                whoFor={whoFor}
                                lastGood={lastGood}
                                expanded={expanded.includes("waiting")}
                                onExpand={expandFor("waiting")}
                                onOpen={open}
                            />
                        )}
                    </>
                )}

                {(everyGroup || !countable) && (loading || (data && data.clients.length > 0)) && (
                    <ClientsCard clients={data?.clients ?? []} loading={loading} note={setupNote ?? (summary ? typicalReplyNote(summary) : null)} tones={tones} onOpen={open} />
                )}
            </div>
        </FoldScope>
    );
}
